/* Babyboo landing layer — behaviour for bb- sections. Loaded as a module. */

const formatMoney = (cents, format) => {
  const value = Number(cents) || 0;
  const fmt = (n, decimals, thousands = ',', decimal = '.') => {
    const fixed = (n / 100).toFixed(decimals);
    const [int, frac] = fixed.split('.');
    const withSep = int.replace(/\B(?=(\d{3})+(?!\d))/g, thousands);
    return frac ? withSep + decimal + frac : withSep;
  };
  return (format || '${{amount}}').replace(/\{\{\s*(\w+)\s*\}\}/, (_, key) => {
    switch (key) {
      case 'amount_no_decimals': return fmt(value, 0);
      case 'amount_with_comma_separator': return fmt(value, 2, '.', ',');
      case 'amount_no_decimals_with_comma_separator': return fmt(value, 0, '.', ',');
      case 'amount_with_apostrophe_separator': return fmt(value, 2, "'", '.');
      case 'amount_no_decimals_with_space_separator': return fmt(value, 0, ' ');
      case 'amount_with_space_separator': return fmt(value, 2, ' ', ',');
      default: return fmt(value, 2);
    }
  });
};

const addDays = (date, days) => {
  const d = new Date(date);
  d.setDate(d.getDate() + days);
  return d;
};

const dateLabel = (from, to) => {
  const locale = document.documentElement.lang || undefined;
  const month = (d) => d.toLocaleDateString(locale, { month: 'short' });
  if (!to || from.toDateString() === to.toDateString()) return `${month(from)} ${from.getDate()}`;
  if (from.getMonth() === to.getMonth()) return `${month(from)} ${from.getDate()}–${to.getDate()}`;
  return `${month(from)} ${from.getDate()}–${month(to)} ${to.getDate()}`;
};

/* ---------- add to cart that plays nicely with Horizon's cart drawer ---------- */
async function addToCart(source, items) {
  let events = null;
  try {
    events = await import('@shopify/events');
  } catch (e) {
    events = null;
  }

  const Ev = events && events.CartLinesUpdateEvent;
  const deferred = Ev ? Ev.createPromise() : null;
  if (Ev) {
    source.dispatchEvent(
      new Ev({
        action: 'add',
        context: 'product',
        lines: items.map((i) => ({ merchandiseId: String(i.id), quantity: i.quantity })),
        promise: deferred.promise,
      })
    );
  }

  const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  const res = await fetch(`${root}cart/add.js`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    body: JSON.stringify({ items }),
  });
  const data = await res.json();
  const cart = await fetch(`${root}cart.js`).then((r) => r.json());
  const failed = !res.ok || data.status;

  if (deferred) {
    deferred.resolve({
      cart: Ev.createCartFromAjaxResponse(cart),
      detail: { items: cart.items, didError: !!failed, source: 'bb-product', itemCount: items[0].quantity },
    });
  }
  if (failed) throw new Error(data.description || data.message || (window.BB_I18N && window.BB_I18N.add_error) || 'Could not add to cart');
  if (!Ev) window.location.href = `${root}cart`;
  return cart;
}

/* ---------- product section ---------- */
class BBProduct extends HTMLElement {
  connectedCallback() {
    if (this._ready) return;
    this._ready = true;
    this.product = JSON.parse(this.querySelector('[data-product-json]').textContent);
    this.format = this.dataset.moneyFormat;
    this.form = this.querySelector('form[data-type="add-to-cart-form"]');
    this.variant =
      this.product.variants.find((v) => String(v.id) === this.querySelector('[data-variant-input]').value) ||
      this.product.variants[0];

    this.querySelectorAll('.bb-option input').forEach((input) =>
      input.addEventListener('change', () => this.onOptionChange())
    );
    this.querySelectorAll('[data-bundle] input').forEach((input) =>
      input.addEventListener('change', () => {
        this.syncQuantity();
        this.renderPrices();
      })
    );
    this.form?.addEventListener('submit', (e) => this.onSubmit(e));

    document.addEventListener('bb:fx', () => this.renderPrices());
    this.initGallery();
    this.initCutoff();
    this.initDelivery();
    this.initSticky();
    this.syncQuantity();
    this.renderPrices();
    this.markUnavailable();
  }

  selectedOptions() {
    return [...this.querySelectorAll('.bb-option')].map((fs) => fs.querySelector('input:checked')?.value);
  }

  onOptionChange() {
    const opts = this.selectedOptions();
    const match = this.product.variants.find((v) => v.options.every((o, i) => o === opts[i]));
    if (!match) return;
    this.variant = match;
    this.querySelector('[data-variant-input]').value = match.id;
    this.renderPrices();
    this.markUnavailable();
    if (this.dataset.variantMedia && match.featured_media) this.showMedia(String(match.featured_media.id));
    if (this.dataset.updateUrl) {
      const url = new URL(window.location.href);
      url.searchParams.set('variant', match.id);
      window.history.replaceState({}, '', url);
    }
  }

  markUnavailable() {
    const btn = this.querySelector('[data-atc]');
    const label = this.querySelector('[data-atc-label]');
    if (!btn || !label) return;
    btn.disabled = !this.variant.available;
    label.textContent = this.variant.available ? label.dataset.add : label.dataset.sold;
  }

  renderPrices() {
    const unit = this.variant.price;
    const was = this.variant.compare_at_price;
    const m = (c) => formatMoney(c, this.format);
    const sel = this.querySelector('[data-bundle] input:checked')?.closest('.bb-bundle__opt');
    const qty = sel ? Number(sel.dataset.qty) : 1;
    const off = sel ? Number(sel.dataset.off) : 0;
    const total = (unit - Math.floor((unit * off) / 100)) * qty;
    const ref = Math.max(was || 0, unit) * qty;
    const now = this.querySelector('[data-price-now]');
    const wasEl = this.querySelector('[data-price-was]');
    const badge = this.querySelector('[data-price-badge]');
    if (now) now.textContent = m(total);
    if (wasEl && badge) {
      const on = ref > total;
      wasEl.hidden = !on;
      badge.hidden = !on;
      if (on) {
        wasEl.textContent = m(ref);
        if (!this.saveWord) this.saveWord = badge.textContent.trim().split(' ')[0] || 'Save';
        badge.textContent = `${this.saveWord} ${Math.round(((ref - total) * 100) / ref)}%`;
      }
    }
    const fx = window.BBFX;
    const fxLine = this.querySelector('[data-fx-line]');
    if (fxLine && fx) {
      const txt = fx.fmt(total);
      fxLine.hidden = !txt;
      const amt = fxLine.querySelector('[data-fx-amount]');
      if (amt) amt.textContent = txt ? (window.BB_I18N?.approx || '≈ [amount]').replace('[amount]', txt) : '';
      const note = this.querySelector('[data-fx-note]');
      if (note) {
        note.hidden = !txt;
        note.textContent = (window.BB_I18N?.approx_note || '').replace('[currency]', fx.cur).replace('[shop_currency]', fx.base);
      }
    }
    const summary = this.querySelector('[data-bundle-summary]');
    if (summary && sel) {
      const title = sel.querySelector('.bb-bundle__title')?.childNodes[0]?.textContent.trim() || `${qty}`;
      summary.innerHTML = `${title}: <strong>${m(total)}</strong>`;
    }
    this.querySelectorAll('[data-bundle] .bb-bundle__opt').forEach((opt) => {
      const qty = Number(opt.dataset.qty);
      const off = Number(opt.dataset.off);
      const full = unit * qty;
      const total = (unit - Math.floor((unit * off) / 100)) * qty;
      opt.querySelector('[data-bundle-total]').textContent = m(total);
      const fullEl = opt.querySelector('[data-bundle-full]');
      if (fullEl) fullEl.textContent = m(full);
      const saved = opt.querySelector('[data-saved]');
      const each = opt.querySelector('[data-each]');
      if (saved) saved.textContent = m(full - total);
      if (each) each.textContent = m(Math.round(total / qty));
      const fxEl = opt.querySelector('[data-fx-bundle]');
      if (fxEl) fxEl.textContent = window.BBFX ? window.BBFX.fmt(total) : '';
    });
    const sticky = this.querySelector('[data-sticky-price]');
    if (sticky) sticky.textContent = m(total);
  }

  syncQuantity() {
    const checked = this.querySelector('[data-bundle] input:checked');
    this.querySelector('[data-qty-input]').value = checked ? checked.value : 1;
    this.querySelectorAll('[data-bundle] .bb-bundle__opt').forEach((o) => o.classList.toggle('is-checked', o.contains(checked)));
  }

  async onSubmit(e) {
    e.preventDefault();
    const btn = this.querySelector('[data-atc]');
    const err = this.querySelector('[data-atc-error]');
    const qty = Number(this.querySelector('[data-qty-input]').value) || 1;
    btn.setAttribute('aria-busy', 'true');
    err.hidden = true;
    try {
      await addToCart(this.form, [{ id: this.variant.id, quantity: qty }]);
    } catch (error) {
      err.textContent = error.message;
      err.hidden = false;
    } finally {
      btn.removeAttribute('aria-busy');
    }
  }

  /* gallery */
  initGallery() {
    this.slides = [...this.querySelectorAll('.bb-gallery__slide')];
    // warm the rest of the gallery once the page is idle
    const warm = () => this.slides.forEach((s) => this.hydrate(s));
    window.addEventListener('load', () => setTimeout(() => ('requestIdleCallback' in window ? requestIdleCallback(warm, { timeout: 4000 }) : warm()), 2500), { once: true });
    this.querySelector('.bb-gallery__stage')?.addEventListener('pointerenter', () => this.hydrate(this.slides[1]), { once: true });
    this.querySelector('[data-prev]')?.addEventListener('click', () => this.step(-1));
    this.querySelector('[data-next]')?.addEventListener('click', () => this.step(1));
    this.querySelectorAll('[data-thumb]').forEach((t) =>
      t.addEventListener('click', () => this.showMedia(t.dataset.thumb))
    );
    const stage = this.querySelector('.bb-gallery__stage');
    let x0 = null;
    stage?.addEventListener('touchstart', (e) => (x0 = e.touches[0].clientX), { passive: true });
    stage?.addEventListener('touchend', (e) => {
      if (x0 === null) return;
      const dx = e.changedTouches[0].clientX - x0;
      if (Math.abs(dx) > 40) this.step(dx < 0 ? 1 : -1);
      x0 = null;
    });
  }

  step(dir) {
    if (!this.slides.length) return;
    const i = this.slides.findIndex((s) => s.classList.contains('is-active'));
    const next = this.slides[(i + dir + this.slides.length) % this.slides.length];
    this.showMedia(next.dataset.mediaId);
  }

  hydrate(slide) {
    const img = slide && slide.querySelector('img[data-src]');
    if (!img) return;
    if (img.dataset.srcset) img.srcset = img.dataset.srcset;
    img.src = img.dataset.src;
    img.removeAttribute('data-src');
    img.removeAttribute('data-srcset');
  }

  showMedia(id) {
    this.slides.forEach((s) => {
      const on = s.dataset.mediaId === id;
      if (on) this.hydrate(s);
      s.classList.toggle('is-active', on);
      const vid = s.querySelector('video');
      if (vid) {
        if (on) {
          if (vid.dataset.src) { vid.src = vid.dataset.src; vid.removeAttribute('data-src'); }
          vid.play().catch(() => {});
        } else vid.pause();
      }
    });
    const i = this.slides.findIndex((s) => s.dataset.mediaId === id);
    this.hydrate(this.slides[(i + 1) % this.slides.length]);
    this.querySelectorAll('[data-thumb]').forEach((t) => {
      const on = t.dataset.thumb === id;
      t.setAttribute('aria-current', on ? 'true' : 'false');
      if (on) t.scrollIntoView({ block: 'nearest', inline: 'nearest', behavior: 'smooth' });
    });
  }

  /* countdown badge */
  initCutoff() {
    const box = this.querySelector('[data-cutoff-box]');
    const left = this.querySelector('[data-cutoff-left]');
    const raw = this.dataset.cutoff;
    if (!box || !left || !raw) return;
    const end = new Date(`${raw}T23:59:59`);
    const tick = () => {
      const ms = end - new Date();
      if (Number.isNaN(ms) || ms <= 0) {
        box.hidden = true;
        return;
      }
      const d = Math.floor(ms / 86400000);
      const h = Math.floor((ms % 86400000) / 3600000);
      const mins = Math.floor((ms % 3600000) / 60000);
      const I = window.BB_I18N || {};
      const tpl = d > 0 ? I.left_days || '[d]D [h]H LEFT' : I.left_hours || '[h]H [m]M LEFT';
      left.textContent = tpl.replace('[d]', d).replace('[h]', h).replace('[m]', mins);
      box.hidden = false;
    };
    tick();
    setInterval(tick, 60000);
  }

  initDelivery() {
    const els = this.querySelectorAll('[data-delivery] [data-d]');
    if (!els.length) return;
    const now = new Date();
    const n = (k) => Number(this.dataset[k]) || 0;
    els[0].textContent = dateLabel(now);
    els[1].textContent = dateLabel(addDays(now, n('shipMin')), addDays(now, n('shipMax')));
    els[2].textContent = dateLabel(addDays(now, n('arriveMin')), addDays(now, n('arriveMax')));
  }

  initSticky() {
    const bar = this.querySelector('[data-sticky]');
    const target = this.querySelector('[data-atc-wrap]');
    if (!bar || !target || !('IntersectionObserver' in window)) return;
    const io = new IntersectionObserver(([entry]) => {
      const below = entry.boundingClientRect.top < 0;
      const show = !entry.isIntersecting && below;
      bar.classList.toggle('is-visible', show);
      bar.setAttribute('aria-hidden', show ? 'false' : 'true');
      bar.querySelector('button').tabIndex = show ? 0 : -1;
    });
    io.observe(target);
    bar.querySelector('[data-sticky-btn]').addEventListener('click', () => {
      (this.querySelector('[data-bundle]') || target).scrollIntoView({ behavior: 'smooth', block: 'center' });
    });
  }
}
if (!customElements.get('bb-product')) customElements.define('bb-product', BBProduct);

/* ---------- before / after slider ---------- */
class BBBeforeAfter extends HTMLElement {
  connectedCallback() {
    const range = this.querySelector('input[type="range"]');
    const box = this.querySelector('.bb-ba');
    if (!range || !box) return;
    const set = () => box.style.setProperty('--pos', `${range.value}%`);
    range.addEventListener('input', set);
    set();
  }
}
if (!customElements.get('bb-before-after')) customElements.define('bb-before-after', BBBeforeAfter);

/* ---------- links that jump to the product picker & preselect a bundle ---------- */
document.addEventListener('click', (e) => {
  const link = e.target.closest('a[data-bb-bundle]');
  if (!link) return;
  const pdp = document.querySelector('bb-product');
  if (!pdp) return;
  e.preventDefault();
  const qty = link.dataset.bbBundle;
  const input = qty && pdp.querySelector(`[data-bundle] input[value="${qty}"]`);
  if (input) {
    input.checked = true;
    input.dispatchEvent(new Event('change', { bubbles: true }));
  }
  (pdp.querySelector('[data-bundle]') || pdp).scrollIntoView({ behavior: 'smooth', block: 'center' });
});

/* ?bundle=N in the URL preselects that pack */
const preset = new URLSearchParams(window.location.search).get('bundle');
if (preset) {
  const input = document.querySelector(`bb-product [data-bundle] input[value="${CSS.escape(preset)}"]`);
  if (input) {
    input.checked = true;
    input.dispatchEvent(new Event('change', { bubbles: true }));
  }
}

/* ---------- visitor currency: display-only conversion (checkout stays in the store currency) ---------- */
const EUR = ['AT','BE','HR','CY','EE','FI','FR','DE','GR','IE','IT','LV','LT','LU','MT','NL','PT','SK','SI','ES','MC','SM','VA','AD','ME','XK'];
const COUNTRY_CUR = {
  US: 'USD', CA: 'CAD', GB: 'GBP', AU: 'AUD', NZ: 'NZD', CH: 'CHF', LI: 'CHF', NO: 'NOK', SE: 'SEK', DK: 'DKK', IS: 'ISK', PL: 'PLN', CZ: 'CZK',
  HU: 'HUF', RO: 'RON', BG: 'BGN', RS: 'RSD', TR: 'TRY', UA: 'UAH', IL: 'ILS', AE: 'AED', SA: 'SAR', QA: 'QAR', KW: 'KWD', JP: 'JPY', KR: 'KRW',
  CN: 'CNY', HK: 'HKD', TW: 'TWD', SG: 'SGD', MY: 'MYR', TH: 'THB', ID: 'IDR', PH: 'PHP', IN: 'INR', VN: 'VND', MX: 'MXN', BR: 'BRL', AR: 'ARS',
  CL: 'CLP', CO: 'COP', PE: 'PEN', ZA: 'ZAR', EG: 'EGP', NG: 'NGN', KE: 'KES', MA: 'MAD', PK: 'PKR', BD: 'BDT',
};
EUR.forEach((c) => (COUNTRY_CUR[c] = 'EUR'));
const FX_PICK = ['USD', 'EUR', 'GBP', 'CAD', 'AUD', 'CHF', 'SEK', 'NOK', 'DKK', 'PLN', 'CZK', 'JPY', 'SGD', 'AED'];
const store = {
  get(k) { try { return JSON.parse(localStorage.getItem(k)); } catch (e) { return null; } },
  set(k, v) { try { localStorage.setItem(k, JSON.stringify(v)); } catch (e) { /* private mode */ } },
};

const BBFX = {
  base: null,
  cur: null,
  rates: null,
  ready: false,
  fmt(cents) {
    if (!this.ready || !this.cur || this.cur === this.base) return '';
    const rate = this.rates[this.cur];
    if (!rate) return '';
    const value = (cents / 100) * rate;
    try {
      return new Intl.NumberFormat(document.documentElement.lang || undefined, { style: 'currency', currency: this.cur, maximumFractionDigits: value >= 1000 ? 0 : 2 }).format(value);
    } catch (e) {
      return `${value.toFixed(2)} ${this.cur}`;
    }
  },
  async init(base) {
    this.base = base;
    let country = store.get('bb_country');
    if (!country) {
      try {
        const r = await fetch('/browsing_context_suggestions.json', { credentials: 'same-origin' });
        const j = await r.json();
        country = j && j.detected_values && j.detected_values.country && j.detected_values.country.handle;
      } catch (e) { /* ignore */ }
      if (!country) {
        const m = (navigator.language || '').match(/-([a-z]{2})$/i);
        country = m ? m[1].toUpperCase() : null;
      }
      if (country) store.set('bb_country', country);
    }
    this.cur = store.get('bb_currency') || COUNTRY_CUR[country] || 'USD';
    const cached = store.get('bb_rates');
    if (cached && cached.base === base && Date.now() - cached.at < 12 * 3600e3) {
      this.rates = cached.rates;
    } else {
      try {
        const r = await fetch(`https://open.er-api.com/v6/latest/${encodeURIComponent(base)}`);
        const j = await r.json();
        if (j && j.rates) {
          this.rates = j.rates;
          store.set('bb_rates', { base, at: Date.now(), rates: j.rates });
        }
      } catch (e) { /* offline: no conversion */ }
    }
    this.ready = !!this.rates;
    document.dispatchEvent(new CustomEvent('bb:fx'));
  },
  setCurrency(c) {
    this.cur = c;
    store.set('bb_currency', c);
    document.dispatchEvent(new CustomEvent('bb:fx'));
  },
};
window.BBFX = BBFX;

const pdpForFx = document.querySelector('bb-product');
if (pdpForFx && pdpForFx.dataset.shopCurrency) {
  const sel = pdpForFx.querySelector('[data-fx-select]');
  if (sel) {
    const opts = new Set(FX_PICK);
    sel.innerHTML = '';
    const fill = () => {
      opts.add(BBFX.cur);
      opts.add(pdpForFx.dataset.shopCurrency);
      sel.innerHTML = [...opts].map((c) => `<option value="${c}"${c === BBFX.cur ? ' selected' : ''}>${c}</option>`).join('');
    };
    document.addEventListener('bb:fx', fill);
    sel.addEventListener('change', () => BBFX.setCurrency(sel.value));
  }
  // Shopify Markets now presents local currencies natively; display-only conversion disabled.
}


/* ---------- 3D preview: load the module on first use ---------- */
let bbPvLoading = null;
const loadPreview = () => (bbPvLoading = bbPvLoading || import(window.BB_PREVIEW_URL));
document.addEventListener('click', async (e) => {
  const opener = e.target.closest('[data-pv-open]');
  if (!opener || window.BBPreviewOpen || !window.BB_PREVIEW_URL) return;
  e.preventDefault();
  opener.setAttribute('aria-busy', 'true');
  try {
    await loadPreview();
    window.BBPreviewOpen && window.BBPreviewOpen(opener);
  } finally {
    opener.removeAttribute('aria-busy');
  }
});
document.addEventListener('pointerover', (e) => {
  if (e.target.closest && e.target.closest('[data-pv-open]') && window.BB_PREVIEW_URL) loadPreview();
}, { passive: true });

/* ---------- decorative animations only run while on screen ---------- */
const startDecor = () => {
  const els = document.querySelectorAll('.bb-lights, .bb-marquee, .bb-3d-open, .bb-btn--glow');
  if (!('IntersectionObserver' in window)) return els.forEach((el) => el.classList.add('is-live'));
  const io = new IntersectionObserver((entries) => entries.forEach((en) => en.target.classList.toggle('is-live', en.isIntersecting)), { rootMargin: '80px' });
  els.forEach((el) => io.observe(el));
};
// keep the first render and image paint free of animation work
const whenIdle = (fn) => ('requestIdleCallback' in window ? requestIdleCallback(fn, { timeout: 3000 }) : setTimeout(fn, 1200));
if (document.readyState === 'complete') whenIdle(startDecor);
else window.addEventListener('load', () => whenIdle(startDecor), { once: true });


/* ---------- below-the-fold images: load when within ~400px of the viewport ---------- */
(() => {
  const imgs = document.querySelectorAll('img[data-lazy-src]');
  const show = (img) => {
    if (img.dataset.lazySrcset) img.srcset = img.dataset.lazySrcset;
    img.src = img.dataset.lazySrc;
    img.removeAttribute('data-lazy-src');
    img.removeAttribute('data-lazy-srcset');
  };
  if (!('IntersectionObserver' in window)) return imgs.forEach(show);
  const io = new IntersectionObserver((entries) => entries.forEach((en) => {
    if (!en.isIntersecting) return;
    const group = en.target.closest('.bb-steps');
    (group ? group.querySelectorAll('img[data-lazy-src]') : [en.target]).forEach((img) => { show(img); io.unobserve(img); });
  }), { rootMargin: '400px 400px' });
  imgs.forEach((img) => io.observe(img));
})();

document.addEventListener('click', (e) => {
  const btn = e.target.closest('[data-rv-more]');
  if (btn) btn.closest('[data-bb-reviews]')?.classList.add('is-open');
});

// loved-on-socials clips: load + play only while on screen, one unmuted at a time
(() => {
  const vids = document.querySelectorAll('[data-bb-socials] video');
  if (!vids.length) return;
  const io = new IntersectionObserver((entries) => entries.forEach(({ target: v, isIntersecting }) => {
    if (isIntersecting) {
      if (v.dataset.src) { v.src = v.dataset.src; v.removeAttribute('data-src'); }
      v.play().catch(() => {});
    } else v.pause();
  }), { threshold: 0.5 });
  vids.forEach((v) => io.observe(v));
  document.addEventListener('click', (e) => {
    const btn = e.target.closest('[data-so-sound]');
    if (!btn) return;
    const v = btn.parentElement.querySelector('video');
    const on = v.muted;
    vids.forEach((o) => { o.muted = true; o.parentElement.querySelector('[data-so-sound]')?.setAttribute('aria-pressed', 'false'); });
    v.muted = !on;
    btn.setAttribute('aria-pressed', on ? 'true' : 'false');
    if (on) v.play().catch(() => {});
  });
})();

/* ---------- Snowmug landing (sm-main) ---------- */
class SMProduct extends HTMLElement {
  connectedCallback() {
    if (this._ready) return;
    this._ready = true;
    this.product = JSON.parse(this.querySelector('[data-sm-product]').textContent);
    this.format = this.dataset.moneyFormat;
    this.tiers = [...this.querySelectorAll('[data-tier]')];
    this.tiers.forEach((t) => {
      t.querySelector('input').addEventListener('change', () => this.pickTier(t));
      t.querySelectorAll('[data-pick]').forEach((s) => s.addEventListener('change', () => this.onPick(s)));
    });
    this.querySelector('[data-sm-form]')?.addEventListener('submit', (e) => this.onSubmit(e));
    this.initGallery();
    this.initCountdown();
    this.tiers.forEach((t) => t.querySelectorAll('[data-pick]').forEach((s) => this.onPick(s, true)));
  }

  variant(id) {
    return this.product.variants.find((v) => String(v.id) === String(id)) || this.product.variants[0];
  }

  pickTier(tier) {
    this.tiers.forEach((t) => t.classList.toggle('is-checked', t === tier));
    this.renderPrices();
  }

  onPick(select, silent) {
    const v = this.variant(select.value);
    const img = select.parentElement.querySelector('[data-pick-img]');
    const src = v.featured_image && (v.featured_image.src || v.featured_image);
    if (img) img.innerHTML = src ? `<img src="${src}${src.includes('?') ? '&' : '?'}width=80" alt="" loading="lazy">` : '';
    if (!silent) {
      if (v.featured_media) this.showMedia(String(v.featured_media.id));
      this.renderPrices();
    }
  }

  current() {
    return this.tiers.find((t) => t.classList.contains('is-checked')) || this.tiers[0];
  }

  // all picks of the active tier, or the first variant qty times when the product has one option value
  lines(tier) {
    const qty = Number(tier.dataset.qty) || 1;
    const picks = [...tier.querySelectorAll('[data-pick]')].map((s) => s.value);
    const ids = picks.length ? picks : Array(qty).fill(String(this.product.variants[0].id));
    const grouped = new Map();
    ids.forEach((id) => grouped.set(id, (grouped.get(id) || 0) + 1));
    return [...grouped].map(([id, quantity]) => ({ id: Number(id), quantity }));
  }

  renderPrices() {
    const money = (c) => formatMoney(c, this.format);
    this.tiers.forEach((t) => {
      const qty = Number(t.dataset.qty) || 1;
      const paid = Number(t.dataset.paid) || qty;
      const picks = [...t.querySelectorAll('[data-pick]')].map((s) => this.variant(s.value));
      const units = picks.length ? picks : Array(qty).fill(this.product.variants[0]);
      // free units are the cheapest ones, as Shopify's buy-X-get-Y discount does
      const sorted = units.map((v) => v.price).sort((a, b) => b - a);
      const price = sorted.slice(0, paid).reduce((a, b) => a + b, 0);
      const compare = units.reduce((a, v) => a + (v.compare_at_price || v.price), 0);
      t.querySelector('[data-tier-price]').textContent = money(price);
      t.querySelector('[data-tier-compare]').textContent = money(compare);
      t.querySelector('[data-tier-save]').textContent = money(compare - price);
    });
  }

  async onSubmit(e) {
    e.preventDefault();
    const btn = this.querySelector('[data-sm-add]');
    const err = this.querySelector('[data-sm-error]');
    btn.setAttribute('aria-busy', 'true');
    err.hidden = true;
    try {
      await addToCart(btn, this.lines(this.current()));
    } catch (error) {
      err.textContent = error.message;
      err.hidden = false;
    } finally {
      btn.removeAttribute('aria-busy');
    }
  }

  initGallery() {
    this.slides = [...this.querySelectorAll('.sm-gallery__slide')];
    const warm = () => this.slides.forEach((s) => this.hydrate(s));
    window.addEventListener('load', () => setTimeout(() => ('requestIdleCallback' in window ? requestIdleCallback(warm, { timeout: 4000 }) : warm()), 2500), { once: true });
    this.querySelector('[data-prev]')?.addEventListener('click', () => this.step(-1));
    this.querySelector('[data-next]')?.addEventListener('click', () => this.step(1));
    this.querySelectorAll('[data-thumb]').forEach((t) => t.addEventListener('click', () => this.showMedia(t.dataset.thumb)));
    const stage = this.querySelector('.sm-gallery__stage');
    let x0 = null;
    stage?.addEventListener('touchstart', (e) => (x0 = e.touches[0].clientX), { passive: true });
    stage?.addEventListener('touchend', (e) => {
      if (x0 === null) return;
      const dx = e.changedTouches[0].clientX - x0;
      if (Math.abs(dx) > 40) this.step(dx < 0 ? 1 : -1);
      x0 = null;
    });
  }

  hydrate(slide) {
    const img = slide && slide.querySelector('img[data-src]');
    if (!img) return;
    if (img.dataset.srcset) img.srcset = img.dataset.srcset;
    img.src = img.dataset.src;
    img.removeAttribute('data-src');
    img.removeAttribute('data-srcset');
  }

  step(dir) {
    if (!this.slides.length) return;
    const i = this.slides.findIndex((s) => s.classList.contains('is-active'));
    this.showMedia(this.slides[(i + dir + this.slides.length) % this.slides.length].dataset.mediaId);
  }

  showMedia(id) {
    if (!this.slides.some((s) => s.dataset.mediaId === id)) return;
    this.slides.forEach((s) => {
      const on = s.dataset.mediaId === id;
      if (on) this.hydrate(s);
      s.classList.toggle('is-active', on);
    });
    this.querySelectorAll('[data-thumb]').forEach((t) => {
      const on = t.dataset.thumb === id;
      t.setAttribute('aria-current', on ? 'true' : 'false');
      if (on) t.scrollIntoView({ block: 'nearest', inline: 'nearest' });
    });
  }

  // counts down to the real end date set in the section; the bar is not rendered after it
  initCountdown() {
    const bar = this.querySelector('[data-sm-countdown]');
    if (!bar) return;
    const end = new Date(bar.dataset.smCountdown).getTime();
    const pad = (n) => String(n).padStart(2, '0');
    const el = (k) => bar.querySelector(`[data-${k}]`);
    const tick = () => {
      const left = Math.max(0, end - Date.now());
      if (!left) { bar.hidden = true; return; }
      const s = Math.floor(left / 1000);
      el('d').textContent = pad(Math.floor(s / 86400));
      el('h').textContent = pad(Math.floor((s % 86400) / 3600));
      el('m').textContent = pad(Math.floor((s % 3600) / 60));
      el('s').textContent = pad(s % 60);
    };
    tick();
    setInterval(tick, 1000);
  }
}
if (!customElements.get('sm-product')) customElements.define('sm-product', SMProduct);
